import { cfImageUrl, type CfImageVariant } from "@/lib/cloudflare-image";
import { cn } from "@/lib/utils";

/**
 * A track's 16:9 artwork, or its language mark on the dark ground the artwork
 * itself uses when the track has none yet - `LN-UI04`.
 *
 * Decorative: the track's title is always printed next to it, so the image
 * carries no alt text a screen reader would hear twice.
 */
export function LearnTrackThumb({
  thumbnailImageId,
  mark,
  variant = "card",
  className,
}: {
  thumbnailImageId: string | null;
  /** The language mark for the fallback, `C` for the C track. */
  mark: string;
  variant?: CfImageVariant;
  className?: string;
}) {
  const src = thumbnailImageId ? cfImageUrl(thumbnailImageId, variant) : "";
  if (src) {
    const image = (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        alt=""
        aria-hidden
        className={cn("aspect-video w-full object-cover", className)}
        src={src}
      />
    );
    if (variant !== "hero" || !thumbnailImageId) return image;
    // `SITE-SPD02`: the hero is a track page's largest paint on a phone, where
    // its slot is about 340 px wide. The 1200 px `hero` file (42 KB) shared the
    // line with the page's stylesheets and landed just after the first paint,
    // so the image painted a frame later; the 640 px `card` file (13 KB) is
    // the nearest variant for that slot (`docs/09` §5.3). Wider screens, where
    // the aside runs full width up to `lg`, keep `hero`.
    return (
      <picture className="block">
        <source media="(max-width: 480px)" srcSet={cfImageUrl(thumbnailImageId, "card")} />
        {image}
      </picture>
    );
  }
  return (
    <div
      aria-hidden
      className={cn(
        "flex aspect-video w-full items-center justify-center bg-neutral-950",
        className,
      )}
    >
      <span className="flex h-20 w-20 items-center justify-center rounded-[var(--radius)] bg-primary font-[family-name:var(--font-heading)] text-4xl font-bold text-white shadow-card">
        {mark}
      </span>
    </div>
  );
}

/** The mark a track shows when it has no artwork: its language, else its slug. */
export function trackMark(track: { language: string | null; trackSlug: string }): string {
  return (track.language ?? track.trackSlug).toUpperCase();
}
